// src/utils/audioProcessing.js
// Real audio analysis using Web Audio API + manual DSP
// No fake data — actual FFT, waveform extraction, frequency analysis

// Decode audio file and return AudioBuffer
export const decodeAudio = async (file) => {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await file.arrayBuffer();
  const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
  return { audioBuffer, audioCtx };
};

// Extract waveform samples (downsampled for display)
export const extractWaveform = (audioBuffer, samples = 400) => {
  const channelData = audioBuffer.getChannelData(0); // mono / left channel
  const blockSize = Math.floor(channelData.length / samples);
  const waveform = [];

  for (let i = 0; i < samples; i++) {
    let min = Infinity, max = -Infinity;
    for (let j = 0; j < blockSize; j++) {
      const v = channelData[i * blockSize + j];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    waveform.push({ sample: i, amp: +((max + min) / 2).toFixed(4), peak: +max.toFixed(4), trough: +min.toFixed(4) });
  }

  return waveform;
};

// Real FFT computation (Cooley-Tukey)
const fft = (re, im) => {
  const n = re.length;
  if (n <= 1) return;
  const halfN = n / 2;
  const reEven = new Float64Array(halfN), imEven = new Float64Array(halfN);
  const reOdd = new Float64Array(halfN), imOdd = new Float64Array(halfN);
  for (let i = 0; i < halfN; i++) {
    reEven[i] = re[2*i]; imEven[i] = im[2*i];
    reOdd[i] = re[2*i+1]; imOdd[i] = im[2*i+1];
  }
  fft(reEven, imEven);
  fft(reOdd, imOdd);
  for (let k = 0; k < halfN; k++) {
    const theta = -2 * Math.PI * k / n;
    const cos = Math.cos(theta), sin = Math.sin(theta);
    const tRe = cos * reOdd[k] - sin * imOdd[k];
    const tIm = sin * reOdd[k] + cos * imOdd[k];
    re[k] = reEven[k] + tRe; im[k] = imEven[k] + tIm;
    re[k + halfN] = reEven[k] - tRe; im[k + halfN] = imEven[k] - tIm;
  }
};

// Compute FFT spectrum from audio buffer
export const computeSpectrum = (audioBuffer, fftSize = 512, maxBins = 80) => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;

  // Take center slice of audio
  const start = Math.floor((channelData.length - fftSize) / 2);
  const slice = channelData.slice(Math.max(0, start), start + fftSize);

  // Apply Hanning window
  const re = new Float64Array(fftSize);
  const im = new Float64Array(fftSize);
  for (let i = 0; i < fftSize; i++) {
    const window = 0.5 * (1 - Math.cos(2 * Math.PI * i / (fftSize - 1)));
    re[i] = (slice[i] || 0) * window;
    im[i] = 0;
  }

  fft(re, im);

  const bins = Math.min(maxBins, fftSize / 2);
  const freqResolution = sampleRate / fftSize;
  const result = [];

  for (let i = 0; i < bins; i++) {
    const mag = Math.sqrt(re[i]*re[i] + im[i]*im[i]) / (fftSize / 2);
    const db = 20 * Math.log10(Math.max(mag, 1e-10));
    result.push({
      freq: +(i * freqResolution).toFixed(0),
      magnitude: +mag.toFixed(6),
      db: +db.toFixed(1),
    });
  }

  return result;
};

// Compute spectrogram (time × frequency)
export const computeSpectrogram = (audioBuffer, fftSize = 256, hopSize = 128, maxCols = 50) => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const cols = Math.min(maxCols, Math.floor(channelData.length / hopSize));
  const freqBins = fftSize / 2;
  const matrix = [];

  for (let col = 0; col < cols; col++) {
    const start = col * hopSize;
    const re = new Float64Array(fftSize);
    const im = new Float64Array(fftSize);
    for (let i = 0; i < fftSize; i++) {
      const window = 0.5 * (1 - Math.cos(2 * Math.PI * i / (fftSize - 1)));
      re[i] = (channelData[start + i] || 0) * window;
    }
    fft(re, im);
    const col_data = [];
    for (let k = 0; k < Math.min(32, freqBins); k++) {
      const mag = Math.sqrt(re[k]*re[k] + im[k]*im[k]) / (fftSize / 2);
      const db = 20 * Math.log10(Math.max(mag, 1e-10));
      col_data.push(Math.max(0, db + 80)); // shift to positive range
    }
    matrix.push({ time: +(col * hopSize / sampleRate).toFixed(2), col_data });
  }

  return { matrix, sampleRate, fftSize, freqBins: Math.min(32, freqBins) };
};

// Audio statistics
export const getAudioStats = (audioBuffer) => {
  const channelData = audioBuffer.getChannelData(0);
  const duration = audioBuffer.duration;
  const sampleRate = audioBuffer.sampleRate;

  let rms = 0, peak = 0, zeroCrossings = 0;
  for (let i = 0; i < channelData.length; i++) {
    rms += channelData[i] * channelData[i];
    if (Math.abs(channelData[i]) > peak) peak = Math.abs(channelData[i]);
    if (i > 0 && ((channelData[i] >= 0) !== (channelData[i-1] >= 0))) zeroCrossings++;
  }
  rms = Math.sqrt(rms / channelData.length);
  const zcRate = zeroCrossings / duration;

  return {
    duration: duration.toFixed(2),
    sampleRate,
    channels: audioBuffer.numberOfChannels,
    samples: channelData.length.toLocaleString(),
    rms: rms.toFixed(4),
    peak: peak.toFixed(4),
    dbPeak: (20 * Math.log10(Math.max(peak, 1e-10))).toFixed(1),
    dbRMS: (20 * Math.log10(Math.max(rms, 1e-10))).toFixed(1),
    zcRate: Math.round(zcRate),
    nyquist: sampleRate / 2,
    bitDepth: '32-bit float (decoded)',
  };
};

// Frequency band energy
export const getBandEnergy = (audioBuffer, fftSize = 1024) => {
  const spectrum = computeSpectrum(audioBuffer, fftSize, 200);
  const sampleRate = audioBuffer.sampleRate;

  const bands = [
    { name: 'Sub Bass', lo: 20, hi: 60, color: 'var(--red)' },
    { name: 'Bass', lo: 60, hi: 250, color: 'var(--orange)' },
    { name: 'Low Mid', lo: 250, hi: 500, color: 'var(--gold)' },
    { name: 'Mid', lo: 500, hi: 2000, color: 'var(--green)' },
    { name: 'Upper Mid', lo: 2000, hi: 4000, color: 'var(--cyan)' },
    { name: 'Presence', lo: 4000, hi: 6000, color: 'var(--blue)' },
    { name: 'Brilliance', lo: 6000, hi: sampleRate / 2, color: 'var(--violet)' },
  ];

  return bands.map(band => {
    const binsInBand = spectrum.filter(b => b.freq >= band.lo && b.freq < band.hi);
    const energy = binsInBand.length > 0
      ? binsInBand.reduce((s, b) => s + b.magnitude, 0) / binsInBand.length
      : 0;
    return { ...band, energy: +(energy * 1000).toFixed(2) };
  });
};

// MFCC (simplified - 13 coefficients)
export const computeMFCC = (audioBuffer, numCoeffs = 13) => {
  const spectrum = computeSpectrum(audioBuffer, 512, 40);
  const mags = spectrum.slice(0, 40).map(b => b.magnitude);
  const logMags = mags.map(m => Math.log(Math.max(m, 1e-10)));

  // DCT
  const mfcc = [];
  for (let n = 0; n < numCoeffs; n++) {
    let sum = 0;
    for (let k = 0; k < logMags.length; k++) {
      sum += logMags[k] * Math.cos(Math.PI * n * (k + 0.5) / logMags.length);
    }
    mfcc.push({ coeff: n, value: +(sum / logMags.length).toFixed(4) });
  }
  return mfcc;
};

// Generate synthetic audio for demos
export const generateSyntheticAudio = (type = 'sine', duration = 2, sampleRate = 44100) => {
  const samples = duration * sampleRate;
  const channelData = new Float32Array(samples);

  switch (type) {
    case 'sine':
      for (let i = 0; i < samples; i++)
        channelData[i] = 0.5 * Math.sin(2 * Math.PI * 440 * i / sampleRate);
      break;
    case 'chord':
      for (let i = 0; i < samples; i++)
        channelData[i] = 0.3 * (Math.sin(2*Math.PI*261*i/sampleRate) + Math.sin(2*Math.PI*329*i/sampleRate) + Math.sin(2*Math.PI*392*i/sampleRate));
      break;
    case 'noise':
      for (let i = 0; i < samples; i++)
        channelData[i] = (Math.random() * 2 - 1) * 0.5;
      break;
    case 'sweep':
      for (let i = 0; i < samples; i++) {
        const freq = 100 + (i / samples) * 3000;
        channelData[i] = 0.4 * Math.sin(2 * Math.PI * freq * i / sampleRate);
      }
      break;
    case 'pulse':
      for (let i = 0; i < samples; i++)
        channelData[i] = (Math.sin(2 * Math.PI * 220 * i / sampleRate) > 0 ? 0.5 : -0.5);
      break;
  }

  // Create offline AudioContext to produce AudioBuffer
  const ctx = new OfflineAudioContext(1, samples, sampleRate);
  const buffer = ctx.createBuffer(1, samples, sampleRate);
  buffer.copyToChannel(channelData, 0);
  return buffer;
};
